import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, forwardRef, Inject, Input, OnInit, Optional, Output } from '@angular/core';
import { ClientFullModel, ClientRequest, ComponentType, CountryModel, HX_COMPONENT_NAME, HxAuthService, HxClientService, HxCoinService } from 'hx-services';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HxToastrService } from '../../../services/toastr.service';
import { NgClass, NgIf } from '@angular/common';
import { NgxMaskDirective } from 'ngx-mask';
import { NgbCollapse, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { HxClientIndicatorComponent } from '../indicator/client-indicator.component';
import { HxClientEditComponent } from '../edit/client-edit.component';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { HxDateSelectComponent } from '../../datepicker/date-select/date-select.component';

@Component({
    selector: 'hx-client-checker',
    templateUrl: './client-checker.component.html',
    styleUrls: ['./client-checker.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => HxClientCheckerComponent), // replace name as appropriate
            multi: true
        }
    ],
  imports: [NgIf, NgClass, FormsModule, NgxMaskDirective, NgbTooltip, HxClientIndicatorComponent, NgbCollapse, HxClientEditComponent, TranslocoPipe, HxDateSelectComponent]
})
export class HxClientCheckerComponent implements OnInit, ControlValueAccessor {
  @Input() disabled = false;
  @Input() compact = false;
  @Input() cityId!: number;
  @Input() label = 'client.name';
  @Input({required: true}) country!: CountryModel;
  @Output() clientChange = new EventEmitter<ClientFullModel | undefined>();

  client?: ClientFullModel;
  isLoading = {
    client: false,
    bonus: false,
    saveBirthDate: false,
  };
  clientPhone?: string;

  isCollapsed = true;

  customPatterns = {
    'X': {
      pattern: new RegExp('/|0|4|5|6|7|/'),
    }, '9': {
      pattern: new RegExp('/|9|/'),
    }, 'N': {
      pattern: new RegExp('^(?!3)'),
    }, '0': {
      pattern: new RegExp('\\d'),
    }
  };
  isCashbox = false;
  coinAmount?: number;
  showBirthDateBtn = false;
  birthDate?: string;
  private timeout?: NodeJS.Timeout;

  constructor(
    private toastr: HxToastrService,
    private clientService: HxClientService,
    private cdr: ChangeDetectorRef,
    private auth: HxAuthService,
    private tr: TranslocoService,
    private coinService: HxCoinService,
    @Optional() @Inject(HX_COMPONENT_NAME) private componentName: string,
  ) {
  }

  ngOnInit(): void {
    this.isCashbox = this.componentName === ComponentType.cb;
  }

  updateClient(): void {
    if (!this.client) {
      return;
    }
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    const cl = this.client.client;
    this.timeout = setTimeout(async () => {
      const result = await this.clientService.saveClient(this.clientService.buildRequest({ client: cl, additionalAddresses: this.client?.additionalAddresses ?? [], additionalContacts: this.client?.additionalContacts ?? [] }));
      const client = await this.clientService.getClient(result.id);
      this.initClient(client);
      this.toastr.success('client.update.success');
    }, 1500);

    this.loadCoin(this.client.client.id);
  }

  clear() {
    this.client = undefined;
    this.clientPhone = undefined;
    this.isLoading.client = false;
    this.clientChange.emit();
  }

  clientChanged(client: ClientFullModel) {
    if (client) {
      this.client = {...client};
      this.isCollapsed = true;
    } else {
      this.isCollapsed = true;
    }
  }

  typedClient(event?: string): void {
    let phone = event;
    if (phone && this.country) {
      const prefix = this.country.phonePrefix.replace(/\D+/g, '');
      if (phone.length === this.country.phoneLength) {
        phone = prefix + phone.replace(/\D+/g, '');
      }
      if (phone.length === (prefix.length + this.country.phoneLength)) {
        this.loadClientByPhone(phone);
      } else {
        this.client = undefined;
      }
    } else {
      this.client = undefined;
    }
  }

  onChange(val: any) {
    // console.log('onChange called', val);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(val: string | undefined): void {
    console.log('[hx-client-checker] writeValue', val);
    if (!val) {
      this.clientPhone = undefined;
      this.client = undefined;
    } else {
      this.clientPhone = val.substring(val.length - this.country.phoneLength);
      this.typedClient(val);
    }
  }

  saveBirthDate() {
      this.isLoading.saveBirthDate = true;
      const cl = this.client?.client;
      if (cl) {
        cl.birthDate = this.birthDate;
        cl.note = this.tr.translate('hx.client-checker.addBirthDateNote');
        this.timeout = setTimeout(async () => {
          const updClient = this.clientService.buildRequest({ client: cl, additionalAddresses: this.client?.additionalAddresses ?? [], additionalContacts: this.client?.additionalContacts ?? [] });
          updClient.isBirthDateUpdated = true;
          const result = await this.clientService.saveClient(updClient);
          const client = await this.clientService.getClient(result.id);
          this.initClient(client);
          this.toastr.success('client.update.success');
          this.isLoading.saveBirthDate = false;
        }, 1000);
      } else {
        this.isLoading.saveBirthDate = false;
      }
  }

  private transformPhone(phone: string): string {
    if (!this.country) {
      return phone;
    }
    return phone.substring(phone.length - this.country.phoneLength);
  }

  private async loadClientByPhone(phone?: string) {
    if (!phone) {
      return;
    }
    this.isLoading.client = true;
    const obj = {
      phone: phone,
      countryId: this.country?.id
    } as ClientRequest;
    try {
      const existingClient = await this.clientService.getClientByPhone(phone);
      if (existingClient) {
        this.initClient(existingClient);
        this.loadCoin(existingClient.client.id);
      }
    } catch (err: any) {
      if (err.error?.message === 'client.notFound') {
        try {
          const result = await this.clientService.saveClient(obj);
          const client = await this.clientService.getClient(result.id);
          this.initClient(client);
          this.coinAmount = 0;
        } catch (err: any) {
          this.clear();
          this.cdr.markForCheck();
          throw err;
        }
      } else {
        throw err;
      }
    }
  }

  private initClient(client: ClientFullModel): void {
    if (!client.isMobile) {
      this.clientPhone = undefined;
      this.client = undefined;
      this.cdr.markForCheck();
      return;
    }
    this.client = client;
    if (this.client.country) {
      this.country = this.client.country;
    }
    const phone = `${this.client.client.phone}`;
    this.clientPhone = this.transformPhone(phone);

    this.onChange(this.clientPhone);
    this.clientChange.emit(this.client);

    this.isLoading.client = false;
    this.cdr.markForCheck();
    this.birthDate = this.client.client.birthDate;
    this.showBirthDateBtn = !this.client.client.birthDate;
  }

  private async loadCoin(clientId: number) {
    if (this.isCashbox) {
      const {brandId, countryId} = this.auth.user.store;
      this.isLoading.bonus = true;
      try {
        this.coinAmount = await this.coinService.getActiveCoins({clientId: clientId, countryId: countryId, brandId: brandId});
      } finally {
        this.isLoading.bonus = false;
        this.cdr.markForCheck();
      }
    }
  }
}
